import type { FunctionComponent, ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useNavigate } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import styled, { createGlobalStyle } from 'styled-components'
import Button, { Variant } from './shared/components/Button/Button'
import { isAuthenticated } from './shared/utils/auth/auth'
import AppBody, { APP_CONTAINER_ID } from './AppBody'
import { FOOTER_ID } from './shared/components/Footer/Footer'
import { SEARCH_BAR_INPUT_ID } from './shared/components/Header/HeaderSearchForm'
import { useIsEmbedded } from './shared/contexts/ui'
import { isContentPage, isDataplatformServicesPage, isSearchPage } from './pages'
import { routing } from './routes'
import AppBar from './shared/components/AppBar/AppBar'
import HeaderMenu from './shared/components/Header/HeaderMenu'
import IdleTimeLogout from './shared/components/IdleTimeLogout/IdleTimeLogout'
import './index.css'
import './shared/assets/fonts/AmsterdamSans/otf/AmsterdamSans-Regular.otf'
import './shared/assets/fonts/AmsterdamSans/otf/AmsterdamSans-Bold.otf'
import './shared/assets/fonts/AmsterdamSans/otf/AmsterdamSans-ExtraBold.otf'
import './shared/assets/fonts/AmsterdamSans/otf/AmsterdamSans-BoldItalic.otf'
import './shared/assets/fonts/AmsterdamSans/otf/AmsterdamSans-Italic.otf'
import './shared/assets/fonts/AmsterdamSans/otf/AmsterdamSans-Light.otf'
import CookieModal from './shared/components/Modal/CookieModal'
import useScrollToTop from './shared/hooks/useScrollToTop'
import { HeaderSearchProvider } from './shared/contexts/HeaderSearch/HeaderSearchContext'
import useCurrentRoute from './shared/hooks/useCurrentRoute'
import usePageTitle from './shared/contexts/pageTitle/usePageTitle'

const StyledContainer = styled.div`
  min-height: 100%;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 0;
`

const SkipNavigationLink = styled(Button)`
  width: auto;
  position: absolute;
  top: 0;
  left: 50%;
  z-index: 30; // on top of header
  transform: translateY(-100%) translateX(-50%);
  transition: transform 0.3s;
  &:focus {
    transform: translateY(0%) translateX(-50%);
  }
`

const GlobalStyleApp = createGlobalStyle`
  :root {
    --dark: #000; // level 7
    --light: #fff; // level 1
    --greyText: #767676; // level 5
    --greyDark: #323232; // level 6
    --greyMedium: #b4b4b4; // level 4
    --greyLight: #e6e6e6; // level 3
    --greyUltraLight: #F0F0F0;
    --greySuperLight: #F4F4F4;
    --blue: #004699;
    --blueLight: #009DEC;
    --blueDark: #00387A;
    --red: #ec0000;
    --green: #00a03c;
    --yellow: #FFE600;
    --orange: #FF9100;
    --header-menu-height: 3.75rem;
    --app-menu-height: var(--header-menu-height);

    @media screen and (min-width: 48rem) {
      --header-menu-height: 4.75rem;
    }
  }

  /* Use border-box sizing by default for all elements */
  *, *::before, *::after {
    box-sizing: border-box;
  }

  /* Display reset for older browsers that don't support certain HTML5 elements */
  article, aside, details, figcaption, figure, footer, header, hgroup, menu, nav, section {
      display: block;
  }

  html {
    line-height: 1.15;
    text-size-adjust: 100%;
  }

  html, body {
    margin: 0;
    height: 100%;
    font-family: 'Amsterdam Sans', sans-serif;
    font-size: 100%; // 16px;
    box-sizing: border-box;
  }

  body {
    scrollbar-gutter: stable;
  }

  input,
  select {
    font-family: inherit;
  }

  h1, h2, h3, h4, h5, h6 {
    margin: 0;
  }

  .root, .root > div {
    min-height: 100%;
    background-color: var(--light);
  }

  ul, dd {
    margin: 0;
    padding: 0;
  }

  ul {
    list-style: none;
  }

  button {
    font-family: inherit;
    cursor: pointer;
    border: none;
  }

  @keyframes shimmer {
    100% {
      transform: translateX(100%);
    }
  }

  @media print {
    #FooterBlock, #header {
      display: none;
    }

    #main > div {
      display: block;
    }
  }


  .leaflet-control-zoom {
    font-family: "Amsterdam Sans", sans-serif;
    font-size: 32px;
    position: absolute;
    right: 4px;
    bottom: 0;
  }

  .leaflet-control-zoom-in, .leaflet-control-zoom-out {
    background-color: white;
    width: 44px;
    height: 44px;
    line-height: 30px;
    text-align: center;
    cursor: pointer;
  }

  .leaflet-touch .leaflet-control-zoom-in, .leaflet-touch .leaflet-control-zoom-out {
    font-family: "Amsterdam Sans", sans-serif;
    font-size: 32px;
    font-weight: 400;
  }

  .leaflet-touch .leaflet-control-layers, .leaflet-touch .leaflet-bar {
    border: none;
  }

  .leaflet-touch .leaflet-bar a {
    width: 44px;
    height: 44px;
    line-height: 38px;
    color: var(--blue)
  }

  .leaflet-bar a {
    color: var(--blue);
    border: 2px solid var(--blue);
    border-radius: 0 !important;
  }

  .leaflet-bar a:first-child {
    border-bottom: none;
  }

  .leaflet-bar a:last-child {
    border-top: 2px solid var(--greyMedium);
    border-bottom: 2px solid var(--blue);
  }

`

interface AppWrapperProps {
  children: ReactNode
}

const AppWrapper: FunctionComponent<AppWrapperProps> = ({ children }) => {
  return <StyledContainer>{children}</StyledContainer>
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 2, refetchOnReconnect: false, refetchOnWindowFocus: false },
  },
})

const App: FunctionComponent = () => {
  useScrollToTop()
  const [showCookieModal, setShowCookieModal] = useState(false)
  const navigate = useNavigate()

  const currentRoute = useCurrentRoute()
  const currentPage = currentRoute?.page
  const isEmbedded = useIsEmbedded()
  const authenticated = isAuthenticated()
  const isHomePage = currentPage === routing.home.page
  const stickyClass = () =>
    currentPage &&
    !isEmbedded &&
    (isSearchPage(currentPage) ||
      isDataplatformServicesPage(currentPage) ||
      isContentPage(currentPage) ||
      isHomePage)
      ? 'sticky'
      : ''

  // Redirect to the 404 page if currentPage isn't set
  if (!currentPage) {
    navigate(routing.notFound.path, { replace: true })
  }

  useEffect(() => {
    const cookiesEnabled = localStorage.getItem('enabledCookies')
    if (!cookiesEnabled) {
      setShowCookieModal(true)
    }
  }, [])

  usePageTitle(currentRoute?.title)

  return (
    <QueryClientProvider client={queryClient}>
      <HeaderSearchProvider>
        <GlobalStyleApp />
        <AppWrapper>
          <CookieModal open={showCookieModal} setOpen={setShowCookieModal} />
          <SkipNavigationLink
            variant={Variant.Primary}
            title="Direct naar: inhoud"
            forwardedAs="a"
            href={`#${APP_CONTAINER_ID}`}
          >
            Direct naar: inhoud
          </SkipNavigationLink>
          <SkipNavigationLink
            variant={Variant.Primary}
            title="Direct naar: zoeken"
            onClick={() => {
              document.getElementById(SEARCH_BAR_INPUT_ID)?.focus()
            }}
          >
            Direct naar: zoeken
          </SkipNavigationLink>
          <SkipNavigationLink
            variant={Variant.Primary}
            title="Direct naar: footer"
            forwardedAs="a"
            href={`#${FOOTER_ID}`}
          >
            Direct naar: footer
          </SkipNavigationLink>
          {!isEmbedded && (
            <AppBar id="header" data-testid="header" className={stickyClass()}>
              <HeaderMenu displayTitle={isHomePage} />
            </AppBar>
          )}
          {authenticated && <IdleTimeLogout timeout={1000 * 60 * 60} />}
          <AppBody className={stickyClass()} />
          <ReactQueryDevtools initialIsOpen={false} />
        </AppWrapper>
      </HeaderSearchProvider>
    </QueryClientProvider>
  )
}

export default App
